import { HELPER_TEXT, LABELS, TOOLTIPS, URL_PATHS } from '@constants'
import { yupResolver } from '@hookform/resolvers/yup'
import { useStore } from '@hooks/useStore'
import { CheckCircle, Error } from '@mui/icons-material'
import {
  Alert,
  Box,
  Button,
  CardActions,
  CardContent,
  CardHeader,
  Collapse,
  Divider,
  FormHelperText,
  Tooltip,
  Typography,
} from '@mui/material'
import { OvCard } from '@components'
import { IOVErrorResponseData, IUserRegister } from '@typings'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { OvFormInput } from 'src/components/core/ov-form-input/ov-form-input'
import { OvFormProvider } from 'src/components/core/ov-form-provider/ov-form-provider'
import { OvForm } from 'src/components/core/ov-form/ov-form'
import styles from './register.form.module.scss'
import { validationSchema } from './validation/register.form.validation'

export const RegisterForm = observer(() => {
  const { auth } = useStore()
  const [formError, setFormError] = useState<string | null>(null)
  const [registrationSuccess, setRegistrationSuccess] = useState(false)
  const [registeredEmail, setRegisteredEmail] = useState<string>('')
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    const period = searchParams.get('period')

    if (period === 'monthly') {
      localStorage.setItem('billing_interval', 'month')
    } else if (period === 'yearly') {
      localStorage.setItem('billing_interval', 'year')
    }
  }, [])

  const form = useForm<IUserRegister>({
    resolver: yupResolver(validationSchema()),
    defaultValues: {
      name: undefined,
      email: '',
      password: '',
      password_confirmation: '',
    },
    mode: 'all',
  })

  const { formState } = form

  const handleRegister: SubmitHandler<IUserRegister> = async data => {
    setFormError(null)

    await auth
      .register(data)
      .then(() => {
        // Show success message - user will login next
        setRegisteredEmail(data.email)
        setRegistrationSuccess(true)
      })
      .catch((errors: IOVErrorResponseData) => handleFormErrors(errors))
  }

  const handleFormErrors = (data: IOVErrorResponseData) => {
    const { message, errors } = data

    if (message) setFormError(message)

    if (errors) {
      const errorKeys = Object.keys(errors)

      errorKeys.map(singleKey =>
        form.setError(singleKey as any, {
          type: 'custom',
          message: errors[singleKey][0],
        })
      )
    }
  }

  const sumitForm = () => {
    form.handleSubmit(handleRegister)()
  }

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <OvCard
        className={styles['register-card']}
        elevation={4}
        sx={{ p: 4 }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <Typography
              variant="h4"
              gutterBottom>
              Account Aangemaakt!
            </Typography>
          </Box>
          <Alert
            severity="success"
            sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              gutterBottom>
              Je account is aangemaakt. We hebben een verificatiemail gestuurd naar:
            </Typography>
            <Typography
              variant="body1"
              sx={{ fontWeight: 'bold', mb: 2 }}>
              {registeredEmail}
            </Typography>
            <Typography variant="body2">
              Check je inbox — soms belandt &apos;ie in spam.
            </Typography>
          </Alert>
          <Tooltip title={TOOLTIPS.GO_TO_LOGIN}>
            <Button
              variant="contained"
              fullWidth
              onClick={() => navigate(URL_PATHS.LOGIN)}
              sx={{ mb: 2 }}>
              Ga naar Inloggen
            </Button>
          </Tooltip>
          <Typography
            variant="body2"
            align="center"
            color="text.secondary">
            Geen mail ontvangen? Check je spam of probeer het opnieuw.
          </Typography>
        </CardContent>
      </OvCard>
    )
  }

  return (
    <OvFormProvider
      form={form}
      onSubmit={sumitForm}
      loading={auth.loading}
      enableNavigationBlocker={false}>
      <OvForm>
        <OvCard
          className={styles['register-card']}
          elevation={4}>
          <CardHeader
            title={LABELS.REGISTER_FORM_TITLE}
            sx={{ pb: 1 }}
          />
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              pt: 2,
            }}>
            <OvFormInput
              name="name"
              inputProps={{ type: 'text' }}
              label={LABELS.NAME}
              required
            />
            <OvFormInput
              name="email"
              inputProps={{ type: 'email' }}
              label={LABELS.EMAIL}
              required
            />
            <OvFormInput
              name="password"
              type="password"
              label={LABELS.PASSWORD}
              helperText={HELPER_TEXT.PASSWORD_REQUIREMENTS}
              required
            />
            <OvFormInput
              name="password_confirmation"
              type="password"
              label={LABELS.PASSWORD_AGAIN}
              helperText={HELPER_TEXT.PASSWORD_CONFIRM}
              required
            />

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center' }}>
              Start gratis -- upgrade op elk moment.
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
              Door te registreren ga je akkoord met ons{' '}
              <a
                href="https://outvoice.nl/privacy/app"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline' }}>
                privacybeleid
              </a>
            </Typography>

            <Collapse in={!!formError}>
              <Alert
                color="error"
                icon={<Error />}>
                <FormHelperText error>{formError}</FormHelperText>
              </Alert>
            </Collapse>
          </CardContent>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              flexDirection: 'column',
              gap: 1,
            }}>
            <Tooltip title={TOOLTIPS.REGISTER_BUTTON}>
              <Button
                disabled={formState.isSubmitting || auth.loading || !formState.isValid}
                variant="contained"
                type="submit"
                fullWidth>
                {LABELS.REGISTER}
              </Button>
            </Tooltip>
          </CardActions>
          <Divider sx={{ '& .MuiDivider-wrapper': { fontFamily: 'Roboto Slab' }, my: 2 }}>
            {LABELS.ALREADY_HAVE_ACCOUNT}
          </Divider>
          <CardActions
            sx={{
              px: 2,
              pb: 2,
              justifyContent: 'center',
            }}>
            <Tooltip title={TOOLTIPS.GO_TO_LOGIN}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate(URL_PATHS.LOGIN)}>
                {LABELS.LOGIN}
              </Button>
            </Tooltip>
          </CardActions>
        </OvCard>
      </OvForm>
    </OvFormProvider>
  )
})
